import {
    AccountIdentity,
    ClassifiableRule,
    collectSimpleAssignments,
    ruleMatchesCategory,
} from "@common/helpers/rules";
import { CategoryGlMappingOverrideRow } from "./useCategoryGlMappingGrid";
import {
    CategoryGlMappingDirection,
    WorkspaceAccount,
    areIdentitiesEqual,
    buildAccountResolver,
} from "./categoryGlMappingGrid.utils";

export interface ReconstructableRule extends ClassifiableRule {
    isDisabled?: boolean | null;
}

/**
 * The matched-accounting-rules predicate shared by row reconstruction and the
 * advanced-mapping classification, so the two stay in lockstep.
 */
export function filterMatchedAccountingRules(
    rules: ReconstructableRule[],
    categoryId: string,
): ReconstructableRule[] {
    const categoryIdSet = new Set([categoryId]);
    return rules.filter(
        (rule) => !rule.isDisabled && ruleMatchesCategory(rule, categoryIdSet),
    );
}

interface ReconstructOverrideRowsParams {
    rules: ReconstructableRule[];
    accounts: WorkspaceAccount[];
    categoryId: string;
}

type EntityDirectionIdentities = Record<
    CategoryGlMappingDirection,
    AccountIdentity | null
>;

interface IdentityGroup {
    entityIds: number[];
    in: AccountIdentity | null;
    out: AccountIdentity | null;
}

/**
 * Rebuilds the per-entity override rows for a subcategory from its matched
 * accounting rules, mirroring the backend read-path resolver. Advanced rule
 * sets (anything `collectSimpleAssignments` rejects) reconstruct to no rows;
 * the caller handles the advanced case separately via `classifyCategoryGlMapping`.
 */
export function reconstructOverrideRows({
    rules,
    accounts,
    categoryId,
}: ReconstructOverrideRowsParams): CategoryGlMappingOverrideRow[] {
    const categoryIdSet = new Set([categoryId]);
    const matchedRules = rules.filter(
        (rule) => !rule.isDisabled && ruleMatchesCategory(rule, categoryIdSet),
    );

    const assignments = collectSimpleAssignments(matchedRules);
    if (!assignments) {
        return [];
    }

    const resolveAccountIdentity = buildAccountResolver(accounts);
    const identitiesByEntityId = new Map<number, EntityDirectionIdentities>();
    for (const assignment of assignments) {
        const identities = identitiesByEntityId.get(assignment.entityId) ?? {
            in: null,
            out: null,
        };
        identities[assignment.direction] = resolveAccountIdentity(
            assignment.action,
            assignment.entityId,
        );
        identitiesByEntityId.set(assignment.entityId, identities);
    }

    return groupEntitiesIntoRows(identitiesByEntityId);
}

function groupEntitiesIntoRows(
    identitiesByEntityId: Map<number, EntityDirectionIdentities>,
): CategoryGlMappingOverrideRow[] {
    const sortedEntityIds = [...identitiesByEntityId.keys()].sort(
        (a, b) => a - b,
    );
    const groups: IdentityGroup[] = [];

    for (const entityId of sortedEntityIds) {
        const identities = identitiesByEntityId.get(entityId);
        if (!identities) {
            continue;
        }
        const matchingGroup = groups.find(
            (group) =>
                areIdentitiesEqual(group.in, identities.in) &&
                areIdentitiesEqual(group.out, identities.out),
        );
        if (matchingGroup) {
            matchingGroup.entityIds.push(entityId);
            continue;
        }
        groups.push({
            entityIds: [entityId],
            in: identities.in,
            out: identities.out,
        });
    }

    return groups.map((group, index) => ({
        id: `reconstructed-row-${index}`,
        entityIds: group.entityIds,
        in: group.in,
        out: group.out,
    }));
}
